import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { DocumentInclusion, DocumentType } from '@/entities/templates';
import { useContentLanguageOptions } from '@/entities/transcriptor';

import {
  DOCUMENT_TYPE_OPTIONS,
  GENERATION_MESSAGES,
  templateEditorMessages,
} from '../../config';

import styles from './TemplateMetaLine.module.scss';

type TemplateMetaLineProps = {
  schemaNames: string[];
  language: string;
  documentType?: DocumentType;
  inclusion?: DocumentInclusion;
  description?: string | null;
  className?: string;
};

type MetaLinePart = {
  key: 'type' | 'language' | 'inclusion' | 'description';
  value: string;
};

export const TemplateMetaLine: FC<TemplateMetaLineProps> = (props) => {
  const {
    schemaNames,
    language,
    documentType,
    inclusion,
    description,
    className,
  } = props;

  const { formatMessage } = useIntl();
  const { getLabel: getLanguageLabel } = useContentLanguageOptions();

  const typeOption = DOCUMENT_TYPE_OPTIONS.find(
    (option) => option.value === documentType,
  );

  const allParts: MetaLinePart[] = [
    {
      key: 'type',
      value: typeOption ? formatMessage(typeOption.labelMessage) : '',
    },
    {
      key: 'language',
      value: getLanguageLabel(language),
    },
    {
      key: 'inclusion',
      value: inclusion ? formatMessage(GENERATION_MESSAGES[inclusion]) : '',
    },
    { key: 'description', value: description?.trim() ?? '' },
  ];
  const parts = allParts.filter((part) => Boolean(part.value));

  return (
    <div
      className={cn(styles.container, className)}
      data-component="template-meta-line"
    >
      <span className={styles.label}>
        {formatMessage(templateEditorMessages.metaSpecialtiesLabel)}
      </span>
      <span>
        {schemaNames.length > 0
          ? schemaNames.join(', ')
          : formatMessage(templateEditorMessages.withoutSpecialty)}
      </span>
      {parts.map((part) => (
        <span key={part.key} className={styles.part}>
          <span className={styles.separator}>·</span>
          <span>{part.value}</span>
        </span>
      ))}
    </div>
  );
};
